/**
 * FR-050…056 · Quản lý người dùng — bám bố cục `users.html`:
 *
 *   page-head (Xuất CSV · Mời người dùng)
 *   section.grid.g-4          4 thẻ `.stat`
 *   section.card.mt-20        card-head[toolbar: filter-pills + search] ·
 *                             table-wrap > table.tbl#users-table · card-foot
 *   modal-invite · modal-user-detail
 *
 * Giữ nguyên nhãn tiếng Việt và `data-od-id` của prototype (docs/08 §8.9).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, apiWithMeta } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { avatarTone, initialsOf, useFormat } from '../../lib/format';
import type { SystemRole } from '../../lib/types';
import { Icon } from '../../components/Icon';
import { SearchBox } from '../../components/SearchBox';
import { Pagination } from '../../components/Pagination';
import { SortButton } from '../../components/SortButton';
import { usePhanTrangUrl } from '../../lib/phan-trang';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { CreateUserModal, DeleteUserModal, InviteUserModal, UserDetailModal } from './UsersModals';
import type { UserListItem, UsersPayload } from './types';

/** OQ-262 — mặc định 25 (docs/04), người dùng đổi được qua ô chọn. */

type Pill = 'all' | 'admin' | 'member' | 'invited' | 'suspended' | 'locked';
/*
 * OQ-255 — `suspended` từng không có lối lọc nào.
 *
 * Bốn trạng thái tài khoản (docs/02 FR-051) nhưng hàng lọc chỉ có `invited` và
 * `locked`. Người bị **tạm ngưng** — do quản trị viên bấm "Vô hiệu hoá" — chỉ
 * hiện ở tab "Tất cả", và bốn thẻ số liệu cũng không thẻ nào đếm họ.
 *
 * Hệ quả: chủ dự án nhìn "5 hoạt động trên tổng 6" rồi tìm người thứ sáu ở
 * "Chờ xác nhận" (0) và "Bị khoá" (0) — không thấy đâu cả.
 *
 * `locked` ≠ `suspended`: một cái do hệ thống tự khoá vì sai mật khẩu quá
 * ngưỡng, một cái do người quản trị chủ động tắt. Gộp chúng lại sẽ che mất
 * đúng thông tin cần biết khi đi tìm nguyên nhân.
 */
const PILLS: Pill[] = ['all', 'admin', 'member', 'invited', 'suspended', 'locked'];

function statusChip(status: string): string {
  if (status === 'active') return 'chip ok';
  if (status === 'invited') return 'chip warn';
  if (status === 'locked') return 'chip bad';
  return 'chip';
}

/** Xuất CSV phía client — docs/04 §4.7 không có endpoint export (OQ-020). */
function exportCsv(rows: UserListItem[], header: string[]): void {
  const cell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = rows.map((row) =>
    [
      row.fullName,
      row.email,
      row.role.name,
      row.status,
      row.twofaEnabled ? 'TOTP' : '',
      row.domainCount,
      row.lastLoginAt ?? '',
    ]
      .map(cell)
      .join(','),
  );
  const blob = new Blob([`\uFEFF${[header.join(','), ...lines].join('\r\n')}\r\n`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `nguoi-dung-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function UsersPage() {
  const { formatDateTime, formatNumber } = useFormat();
  const t = useT();
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const [pill, setPill] = useState<Pill>('all');
  const [search, setSearch] = useState('');
  const { params, page, perPage, datTrang, datSoDong, datLoc } = usePhanTrangUrl();
  const sort = params.get('sort') ?? '';
  const [selected, setSelected] = useState<string[]>([]);

  const [showInvite, setShowInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [deleteFor, setDeleteFor] = useState<UserListItem | null>(null);

  // `GET /roles` cần capability adm.roles — không có thì suy danh mục vai trò
  // từ chính danh sách người dùng (OQ-027), không bịa dữ liệu.
  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<SystemRole[]>('/roles'),
    enabled: can('adm.roles'),
    staleTime: 5 * 60 * 1000,
  });

  const query = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (search.trim()) params.set('q', search.trim());
    if (pill === 'invited') params.set('status', 'invited');
    if (pill === 'suspended') params.set('status', 'suspended');
    if (pill === 'locked') params.set('status', 'locked');
    if (sort) params.set('sort', sort);
    return params;
    /*
     * `perPage` PHẢI có trong deps — OQ-262.
     *
     * Thiếu nó thì `useMemo` giữ chuỗi cũ khi người dùng đổi số dòng: `queryKey`
     * không đổi, React Query không gọi lại, bảng vẫn 25 dòng dù ô chọn hiện 100.
     * Không lỗi nào để lần theo, chỉ có một cái ô trông như hỏng.
     *
     * Ca e2e "đổi số dòng thì bảng nạp lại thật" bắt đúng chỗ này — lỗi mà mọi
     * ca đọc mã nguồn đều bỏ qua, vì cấu trúc nhìn hoàn toàn đúng. `sort` thêm ở
     * OQ-266 cũng nằm trong deps vì đúng lý do ấy.
     */
  }, [page, perPage, search, pill, sort]);

  const usersQuery = useQuery({
    queryKey: ['users', query.toString()],
    queryFn: () => apiWithMeta<UsersPayload>(`/users?${query.toString()}`),
  });

  const payload = usersQuery.data?.data;
  const meta = usersQuery.data?.meta;

  const roleOptions = useMemo(() => {
    if (rolesQuery.data) {
      return rolesQuery.data.map((role) => ({ id: role.id, slug: role.slug, name: role.name }));
    }
    const seen = new Map<string, { id: string; slug: string; name: string }>();
    for (const item of payload?.items ?? []) seen.set(item.role.id, item.role);
    return [...seen.values()];
  }, [rolesQuery.data, payload]);

  // Hai viên "Quản trị" / "Thành viên" lọc theo vai trò, lọc ngay trên trang
  // hiện tại để không phát sinh tham số ngoài hợp đồng docs/04 §4.7.
  const items = useMemo(() => {
    const rows = payload?.items ?? [];
    if (pill === 'admin') return rows.filter((row) => row.role.slug === 'admin');
    if (pill === 'member') return rows.filter((row) => row.role.slug === 'member');
    return rows;
  }, [payload, pill]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const patchUser = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api.patch<UserListItem>(`/users/${input.id}`, input.body),
    onSuccess: async () => {
      await invalidate();
    },
    onError,
  });

  const forceTwoFactor = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/force-2fa`),
    onSuccess: async () => {
      await invalidate();
      toast(t('users.toast.force2fa'));
    },
    onError,
  });

  const resendInvite = useMutation({
    mutationFn: (id: string) => api.post<{ email: string }>(`/users/${id}/resend-invite`),
    onSuccess: async (data) => {
      await invalidate();
      toast(t('users.toast.resent', { email: data.email }));
    },
    onError,
  });

  const bulkForceTwoFactor = async () => {
    const targets = items.filter((row) => selected.includes(row.id) && !row.twofaEnabled);
    for (const row of targets) await forceTwoFactor.mutateAsync(row.id).catch(() => undefined);
    setSelected([]);
  };

  const bulkSuspend = async () => {
    const targets = items.filter((row) => selected.includes(row.id) && row.id !== user?.id);
    for (const row of targets) {
      await patchUser
        .mutateAsync({ id: row.id, body: { status: 'suspended' } })
        .catch(() => undefined);
    }
    setSelected([]);
    toast(t('users.toast.suspendedMany', { count: targets.length }));
  };

  /*
   * Mở lại tài khoản — nghịch đảo của `bulkSuspend`.
   *
   * Lọc theo `status !== 'active'` chứ không chỉ `'suspended'`: tài khoản bị
   * KHOÁ do nhập sai mật khẩu quá số lần cũng cần lối mở lại, và `PATCH` với
   * `status: 'active'` là đúng thao tác cho cả hai. Bỏ tài khoản `invited` —
   * chúng chưa từng hoạt động nên "mở lại" không có nghĩa; chúng cần
   * "Gửi lại lời mời".
   */
  const bulkActivate = async () => {
    const targets = items.filter(
      (row) => selected.includes(row.id) && row.status !== 'active' && row.status !== 'invited',
    );
    for (const row of targets) {
      await patchUser
        .mutateAsync({ id: row.id, body: { status: 'active' } })
        .catch(() => undefined);
    }
    setSelected([]);
    toast(t('users.toast.activatedMany', { count: targets.length }));
  };

  /* Số bản ghi mỗi nút sẽ thật sự đụng tới — để nút tự tắt khi không có việc. */
  const soCanMoLai = items.filter(
    (row) => selected.includes(row.id) && row.status !== 'active' && row.status !== 'invited',
  ).length;
  const soCanVoHieu = items.filter(
    (row) => selected.includes(row.id) && row.status === 'active' && row.id !== user?.id,
  ).length;

  const stats = payload?.stats;
  const twofaPercent =
    stats && stats.total > 0 ? Math.round((stats.twofaEnabled / stats.total) * 100) : 0;

  return (
    <div data-od-id="page-users">
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="users-title">{t('users.title')}</h1>
          <p className="lede">{t('users.lede')}</p>
        </div>
        <div className="row">
          <button
            className="btn"
            type="button"
            data-od-id="users-export"
            disabled={items.length === 0}
            onClick={() => {
              exportCsv(items, [
                t('users.col.name'),
                'email',
                t('users.col.role'),
                t('users.col.status'),
                '2FA',
                t('users.col.domains'),
                t('users.col.lastLogin'),
              ]);
              toast(t('users.toast.exported'));
            }}
          >
            {t('users.export')}
          </button>
          <button
            className="btn"
            type="button"
            data-od-id="users-create"
            onClick={() => setShowCreate(true)}
          >
            <Icon name="user-plus" size={16} />
            {t('users.create')}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            data-od-id="users-invite"
            onClick={() => setShowInvite(true)}
          >
            <Icon name="plus" size={16} />
            {t('users.invite')}
          </button>
        </div>
      </div>

      <section className="grid g-5" data-od-id="users-stats">
        <div className="card stat">
          <div className="label">{t('users.stat.active')}</div>
          <div className="value num">{formatNumber(stats?.active ?? 0)}</div>
          <div className="delta">
            {t('users.stat.activeSub', { total: formatNumber(stats?.total ?? 0) })}
          </div>
        </div>
        <div className="card stat">
          <div className="label">{t('users.stat.twofa')}</div>
          <div className="value num">{formatNumber(stats?.twofaEnabled ?? 0)}</div>
          <div className="bar ok mt-14">
            <span style={{ width: `${twofaPercent}%` }} />
          </div>
          <div className="delta">{t('users.stat.twofaSub', { percent: twofaPercent })}</div>
        </div>
        <div className="card stat">
          <div className="label">{t('users.stat.invited')}</div>
          <div className="value num">{formatNumber(stats?.invited ?? 0)}</div>
          <div className={stats?.invitesExpired ? 'delta warn' : 'delta'}>
            {stats?.invitesExpired ? (
              <>
                <b>{stats.invitesExpired}</b>
                <span>{t('users.stat.invitedExpired')}</span>
              </>
            ) : (
              t('users.stat.invitedSub')
            )}
          </div>
        </div>
        <div className="card stat">
          <div className="label">{t('users.stat.suspended')}</div>
          <div className="value num">{formatNumber(stats?.suspended ?? 0)}</div>
          <div className="delta">{t('users.stat.suspendedSub')}</div>
        </div>
        <div className="card stat">
          <div className="label">{t('users.stat.locked')}</div>
          <div className="value num">{formatNumber(stats?.locked ?? 0)}</div>
          <div className="delta">{t('users.stat.lockedSub')}</div>
        </div>
      </section>

      <section className="card mt-20" data-od-id="users-table-card">
        <div className="card-head">
          <div className="toolbar grow">
            <div className="filter-pills" data-od-id="users-filter">
              {PILLS.map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={pill === name}
                  onClick={() => {
                    setPill(name);
                    datLoc({});
                    setSelected([]);
                  }}
                >
                  {t(`users.filter.${name}`)}
                </button>
              ))}
            </div>
            <SearchBox
              value={search}
              onChange={(next) => {
                setSearch(next);
                datLoc({});
              }}
              placeholder={t('users.searchPlaceholder')}
              width={230}
            />
          </div>
          <span className="small muted num">
            {items.length} / {formatNumber(meta?.total ?? 0)}
          </span>
        </div>

        <AsyncSection
          loading={usersQuery.isLoading}
          error={usersQuery.error}
          data={items.length > 0 ? items : undefined}
          onRetry={() => void usersQuery.refetch()}
          skeleton={
            <div className="card-body">
              <SkeletonCard rows={6} />
            </div>
          }
          empty={
            <EmptyState
              icon="users"
              title={t(search || pill !== 'all' ? 'users.empty.filter' : 'users.empty.title')}
              body={t(search || pill !== 'all' ? 'users.empty.filterBody' : 'users.empty.body')}
              action={
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => setShowInvite(true)}
                >
                  {t('users.invite')}
                </button>
              }
            />
          }
        >
          {(rows) => (
            <div className="table-wrap">
              <table className="tbl" id="users-table" data-od-id="users-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input
                        type="checkbox"
                        className="check"
                        aria-label={t('users.selectAll')}
                        checked={selected.length > 0 && selected.length === rows.length}
                        onChange={(event) =>
                          setSelected(event.target.checked ? rows.map((row) => row.id) : [])
                        }
                      />
                    </th>
                    <th scope="col">
                      <SortButton
                        label={t('users.col.user')}
                        field="name"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col">
                      <SortButton
                        label={t('users.col.role')}
                        field="role"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col" className="right">
                      <SortButton
                        label={t('users.col.domains')}
                        field="domains"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col">
                      <SortButton
                        label={t('users.col.twofa')}
                        field="twofa"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col">
                      <SortButton
                        label={t('users.col.lastLogin')}
                        field="lastLogin"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col">
                      <SortButton
                        label={t('users.col.status')}
                        field="status"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th scope="col" className="right">
                      {t('users.col.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="check"
                          aria-label={t('users.selectOne', { name: row.fullName || row.email })}
                          checked={selected.includes(row.id)}
                          onChange={(event) =>
                            setSelected((prev) =>
                              event.target.checked
                                ? [...prev, row.id]
                                : prev.filter((id) => id !== row.id),
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="cell-main">
                          <span className={`avatar sm ${avatarTone(row.id)}`}>
                            {initialsOf(row.fullName, row.email)}
                          </span>
                          <div>
                            {/*
                              OQ-256 — bấm tên là mở chi tiết.
                              Hộp thoại đã có sẵn từ M2 và nút "Chi tiết" ở cột
                              Thao tác vẫn dùng đúng nó; chỗ này chỉ thêm lối vào
                              thứ hai, ở nơi người ta bấm theo bản năng.
                            */}
                            <b>
                              <button
                                type="button"
                                className="ten-bam-duoc"
                                data-od-id="user-name-link"
                                onClick={() => setDetailId(row.id)}
                                title={t('users.action.detail')}
                              >
                                {row.fullName || row.email}
                              </button>
                            </b>
                            <div className="sub">{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={
                            row.role.slug === 'admin' ? 'chip accent tag-role' : 'chip tag-role'
                          }
                        >
                          {row.role.name}
                        </span>
                      </td>
                      <td className="right num">{formatNumber(row.domainCount)}</td>
                      <td>
                        {row.twofaEnabled ? (
                          <span className="chip ok">
                            <span className="dot" />
                            TOTP
                          </span>
                        ) : row.twofaRequired ? (
                          <span className="chip warn">
                            <span className="dot" />
                            {t('users.twofa.required')}
                          </span>
                        ) : row.status === 'invited' ? (
                          <span className="chip">{t('users.twofa.none')}</span>
                        ) : (
                          <span className="chip warn">
                            <span className="dot" />
                            {t('users.twofa.off')}
                          </span>
                        )}
                      </td>
                      <td className={row.lastLoginAt ? 'small num' : 'small muted'}>
                        {row.lastLoginAt ? formatDateTime(row.lastLoginAt) : t('users.neverLogin')}
                      </td>
                      <td>
                        <span className={statusChip(row.status)}>
                          {row.status !== 'suspended' ? <span className="dot" /> : null}
                          {t(`users.status.${row.status}`)}
                        </span>
                      </td>
                      <td className="right">
                        <div className="row-actions">
                          {row.id === user?.id ? (
                            <span className="small muted">{t('users.action.self')}</span>
                          ) : row.status === 'invited' ? (
                            <>
                              <button
                                className="btn btn-sm"
                                type="button"
                                disabled={resendInvite.isPending}
                                onClick={() => resendInvite.mutate(row.id)}
                              >
                                {t('users.action.resend')}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                type="button"
                                onClick={() => setDeleteFor(row)}
                              >
                                {t('users.action.revoke')}
                              </button>
                            </>
                          ) : row.status === 'locked' ? (
                            <button
                              className="btn btn-sm"
                              type="button"
                              disabled={patchUser.isPending}
                              onClick={() =>
                                patchUser.mutate(
                                  { id: row.id, body: { status: 'active' } },
                                  { onSuccess: () => toast(t('users.toast.unlocked')) },
                                )
                              }
                            >
                              {t('users.action.unlock')}
                            </button>
                          ) : (
                            <>
                              <Link className="btn btn-sm" to="/permissions">
                                {t('users.action.permissions')}
                              </Link>
                              <button
                                className="btn btn-sm btn-ghost"
                                type="button"
                                onClick={() => setDetailId(row.id)}
                              >
                                {t('users.action.detail')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncSection>

        <div className="card-foot">
          <span className="small muted">
            {t('users.foot.selected', { count: selected.length })}
          </span>
          <span style={{ flex: 1 }} />
          {/*
            OQ-262 — luôn hiện, kể cả khi chỉ có một trang.

            Trước đây cả cụm ẩn đi khi `totalPages <= 1`. Nay có thêm ô chọn số
            dòng, và ô ấy phải dùng được ngay cả lúc danh sách ngắn — người dùng
            đổi 25 → 100 để khỏi phải bấm trang, chứ không phải chờ có nhiều
            trang rồi mới được đổi.

            Chỉ báo "Trang 1/1" giữ cho hai nút mờ không trông như hỏng.
          */}
          <Pagination
            page={page}
            perPage={perPage}
            total={meta?.total ?? 0}
            odId="users-pager"
            onPage={datTrang}
            onPerPage={datSoDong}
          />
          <button
            className="btn btn-sm"
            type="button"
            disabled={selected.length === 0}
            onClick={() => void bulkForceTwoFactor()}
          >
            {t('users.bulk.force2fa')}
          </button>
          <button
            className="btn btn-sm"
            type="button"
            data-od-id="bulk-activate"
            /*
             * Tắt khi KHÔNG CÓ VIỆC, không phải khi chưa chọn ai: chọn ba tài
             * khoản đang hoạt động rồi bấm "Mở lại" mà không có gì xảy ra thì
             * người dùng không biết là mình chọn sai hay nút hỏng.
             */
            disabled={soCanMoLai === 0}
            title={soCanMoLai > 0 ? t('users.bulk.activateHint', { count: soCanMoLai }) : undefined}
            onClick={() => void bulkActivate()}
          >
            {t('users.bulk.activate')}
            {soCanMoLai > 0 ? ` (${soCanMoLai})` : ''}
          </button>
          <button
            className="btn btn-sm btn-danger"
            type="button"
            data-od-id="bulk-suspend"
            disabled={soCanVoHieu === 0}
            title={
              soCanVoHieu > 0 ? t('users.bulk.suspendHint', { count: soCanVoHieu }) : undefined
            }
            onClick={() => void bulkSuspend()}
          >
            {t('users.bulk.suspend')}
            {soCanVoHieu > 0 ? ` (${soCanVoHieu})` : ''}
          </button>
        </div>
      </section>

      {showCreate ? (
        <CreateUserModal
          roles={roleOptions}
          onClose={() => setShowCreate(false)}
          onDone={invalidate}
        />
      ) : null}

      {showInvite ? (
        <InviteUserModal
          roles={roleOptions}
          onClose={() => setShowInvite(false)}
          onDone={async (email) => {
            await invalidate();
            setShowInvite(false);
            toast(t('users.toast.invited', { email }));
          }}
        />
      ) : null}

      {detailId ? (
        <UserDetailModal
          userId={detailId}
          roles={roleOptions}
          onClose={() => setDetailId(null)}
          onChanged={invalidate}
          onDelete={(target) => {
            setDetailId(null);
            setDeleteFor(target);
          }}
        />
      ) : null}

      {deleteFor ? (
        <DeleteUserModal
          target={deleteFor}
          onClose={() => setDeleteFor(null)}
          onDone={async () => {
            await invalidate();
            setDeleteFor(null);
            toast(t('users.toast.deleted'));
          }}
        />
      ) : null}
    </div>
  );
}
